/**
 * GTM Container Template generator.
 *
 * Produces a JSON file in Google's "Container Export v2" format that
 * the operator imports via:
 *   GTM admin → Workspace → Admin → Import Container → Choose file
 *      → Workspace = "HitzLink Managed" (or new) → Merge → Confirm
 *
 * Why generate it client-side instead of shipping a static template:
 *   The template references the operator's CONFIGURED Pixel IDs and
 *   GA4 measurement ID — those are page-specific. Static template
 *   would force the operator to manually edit every tag after import,
 *   defeating the whole "zero-touch tracking" promise.
 *
 * Why a custom JSON builder instead of an SDK call:
 *   Google has a Tag Manager API (v2) that can do this server-side
 *   with OAuth, but that's Phase 3 work (OAuth flow + refresh token
 *   storage). The container Export format is well-documented and
 *   stable — build the JSON ourselves, the operator imports it once
 *   manually, done.
 *
 * Format references:
 *   • Container Export schema:
 *     https://developers.google.com/tag-manager/api/v2/reference/accounts/containers/versions
 *   • Pixel tag template type "fb" was removed from GTM templates
 *     in 2023; we use Custom HTML tags instead, which work on every
 *     GTM container without requiring template gallery imports.
 *
 * Audit considerations:
 *   • Tag IDs are deterministic so re-importing produces the same
 *     IDs — minimises noise in GTM's version diff.
 *   • Fingerprints are timestamp-based but identical within one
 *     export so all entities show "created at the same time".
 *   • Container path is a placeholder; GTM rewrites it on import.
 */

import {
  GTM_DATALAYER_EVENTS,
  GTM_DATALAYER_KEYS,
} from './destinationSnippets';

interface GtmTemplateOptions {
  pageTitle: string;
  pageId: string;
  /** All Pixel IDs to fan out across — one Pixel tag per ID. */
  pixelIds: string[];
  /** Optional GA4 measurement ID. Empty string = skip GA4 tags. */
  googleAnalyticsId: string;
  /** Container ID we're targeting. Used in the export `path` only;
   *  GTM rewrites on import. */
  gtmId: string;
}

// Stable ID space inside the container. GTM accepts arbitrary string
// IDs — we use namespaced numeric strings so re-imports overwrite
// rather than duplicate. Keep these unique per entity TYPE; collisions
// across types are fine (GTM scopes by type).
const ID = {
  // Triggers
  triggerPageView: '100',
  triggerRegister: '101',
  triggerPurchase: '102',
  // Variables
  varEventId: '200',
  varClickId: '201',
  varValue: '202',
  varCurrency: '203',
  varTxnId: '204',
  varExternalId: '205',
  // Tag IDs are computed dynamically per Pixel ID.
};

function nowFingerprint(): string {
  return String(Date.now());
}

/**
 * Validate a Pixel ID — Meta Pixel IDs are 15-16 digit numeric strings.
 * We're permissive (accept any non-empty digit string up to 20 chars)
 * to handle test pixels and future format changes.
 */
function sanitizePixelId(id: string): string {
  return String(id || '').replace(/[^0-9]/g, '').slice(0, 20);
}

function sanitizeGa4Id(id: string): string {
  // GA4 IDs match /^G-[A-Z0-9]+$/. Old UA IDs (UA-XXXXX-Y) are no
  // longer accepted by Google's collection endpoint, but we still
  // pass them through with sanitisation in case the operator hasn't
  // migrated yet — GA4 will silently drop, no harm done.
  return String(id || '').toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 20);
}

function jsString(s: string): string {
  // Escape for embedding into a JS string literal (used in Custom HTML
  // tag bodies). Single quote + backslash + line terminators only —
  // we control all input so HTML injection isn't a concern.
  return s.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\n/g, '\\n');
}

/**
 * Build the JSON content of the GTM Container Export.
 *
 * Returns a string ready for download as `.json`. The shape conforms
 * to the format you get from GTM admin → Workspace → Admin → Export
 * Container, which is also what GTM's Import Container tool consumes.
 */
export function buildGtmContainerTemplate(opts: GtmTemplateOptions): string {
  const fp = nowFingerprint();
  const safeGtm = String(opts.gtmId || 'GTM-XXXXXXX').toUpperCase().replace(/[^A-Z0-9-]/g, '');
  const cleanPixels = opts.pixelIds
    .map(sanitizePixelId)
    .filter((id) => id.length >= 8); // sanity floor — Meta Pixel IDs are always >= 13 digits
  const ga4Id = sanitizeGa4Id(opts.googleAnalyticsId);

  // Common preamble for all our Custom HTML tags. Reads the eventID /
  // value DLVs at fire time so a single tag definition serves every
  // PageView / CompleteRegistration regardless of which page fired it.
  const PIXEL_TAG_HTML = (
    pixelId: string,
    metaEventName: string,
    options: { withValue?: boolean; withTransaction?: boolean } = {}
  ): string => {
    const lines: string[] = [];
    lines.push('<script>');
    lines.push("(function(){");
    lines.push("  if (!window.fbq) {");
    lines.push("    // Pixel base — load lazily on first event.");
    lines.push('    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?');
    lines.push('    n.callMethod.apply(n,arguments):n.queue.push(arguments)};');
    lines.push("    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';");
    lines.push('    n.queue=[];t=b.createElement(e);t.async=!0;');
    lines.push('    t.src=v;s=b.getElementsByTagName(e)[0];');
    lines.push("    s.parentNode.insertBefore(t,s)}(window, document,'script',");
    lines.push("    'https://connect.facebook.net/en_US/fbevents.js');");
    lines.push(`    var ext = {{${GTM_DATALAYER_KEYS.externalId}}} || '';`);
    lines.push(`    fbq('init', '${pixelId}', ext ? { external_id: ext } : {});`);
    lines.push("  }");
    lines.push(`  var eventId = {{${GTM_DATALAYER_KEYS.eventId}}} || '';`);
    if (options.withValue) {
      lines.push('  var custom = {');
      lines.push(`    value:    Number({{${GTM_DATALAYER_KEYS.value}}}) || 0,`);
      lines.push(`    currency: {{${GTM_DATALAYER_KEYS.currency}}} || 'USD'`);
      if (options.withTransaction) {
        lines.push(`    , content_ids: [{{${GTM_DATALAYER_KEYS.transactionId}}}]`);
      }
      lines.push('  };');
      lines.push(`  if (eventId) {`);
      lines.push(`    fbq('track', '${metaEventName}', custom, { eventID: eventId });`);
      lines.push(`  } else {`);
      lines.push(`    fbq('track', '${metaEventName}', custom);`);
      lines.push(`  }`);
    } else {
      lines.push(`  if (eventId) {`);
      lines.push(`    fbq('track', '${metaEventName}', {}, { eventID: eventId });`);
      lines.push(`  } else {`);
      lines.push(`    fbq('track', '${metaEventName}');`);
      lines.push(`  }`);
    }
    lines.push("})();");
    lines.push('</script>');
    return lines.join('\n');
  };

  // ── Variables (DLVs the snippets push) ────────────────────────────
  // GTM Import Container uses a slightly different enum format than the
  // REST API resource shape: parameter/filter/built-in enum values must
  // be SNAKE_CASE_IN_CAPS (`TEMPLATE`, `BOOLEAN`, `CUSTOM_EVENT`, etc.).
  // The API accepts lowercase (`template`) for live create/update calls,
  // but importing this JSON in the GTM UI rejects lowercase with:
  //   Error deserializing enum type [Type]. Unrecognized value [template].
  // Keep the manual export format uppercase here; server/services/gtmSync.js
  // intentionally keeps lowercase because it talks to the REST API.
  const dlv = (id: string, name: string, key: string) => ({
    accountId: '0',
    containerId: '0',
    variableId: id,
    name,
    type: 'v',
    parameter: [
      { type: 'INTEGER', key: 'dataLayerVersion', value: '2' },
      { type: 'BOOLEAN', key: 'setDefaultValue', value: 'false' },
      { type: 'TEMPLATE', key: 'name', value: key },
    ],
    fingerprint: fp,
  });

  const variables = [
    dlv(ID.varEventId,    `DLV - ${GTM_DATALAYER_KEYS.eventId}`,       GTM_DATALAYER_KEYS.eventId),
    dlv(ID.varClickId,    `DLV - ${GTM_DATALAYER_KEYS.clickId}`,       GTM_DATALAYER_KEYS.clickId),
    dlv(ID.varExternalId, `DLV - ${GTM_DATALAYER_KEYS.externalId}`,    GTM_DATALAYER_KEYS.externalId),
    dlv(ID.varValue,      `DLV - ${GTM_DATALAYER_KEYS.value}`,         GTM_DATALAYER_KEYS.value),
    dlv(ID.varCurrency,   `DLV - ${GTM_DATALAYER_KEYS.currency}`,      GTM_DATALAYER_KEYS.currency),
    dlv(ID.varTxnId,      `DLV - ${GTM_DATALAYER_KEYS.transactionId}`, GTM_DATALAYER_KEYS.transactionId),
  ];

  // ── Triggers (Custom Event matchers for our hitzlink_* events) ────
  const customEventTrigger = (id: string, name: string, eventName: string) => ({
    accountId: '0',
    containerId: '0',
    triggerId: id,
    name,
    type: 'CUSTOM_EVENT',
    customEventFilter: [
      {
        type: 'EQUALS',
        parameter: [
          { type: 'TEMPLATE', key: 'arg0', value: '{{_event}}' },
          { type: 'TEMPLATE', key: 'arg1', value: eventName },
        ],
      },
    ],
    fingerprint: fp,
  });

  const triggers = [
    customEventTrigger(ID.triggerPageView, 'CE - HitzLink PageView',     GTM_DATALAYER_EVENTS.pageView),
    customEventTrigger(ID.triggerRegister, 'CE - HitzLink Registration', GTM_DATALAYER_EVENTS.completeRegistration),
    customEventTrigger(ID.triggerPurchase, 'CE - HitzLink Purchase',     GTM_DATALAYER_EVENTS.purchase),
  ];

  // ── Tags ──────────────────────────────────────────────────────────
  // For each Pixel ID we ship 3 tags (PageView, CompleteRegistration,
  // Purchase). Plus optional GA4 tags (sign_up, purchase, page_view).
  const tags: Array<Record<string, unknown>> = [];
  let tagIdCounter = 1000;
  const nextTagId = () => String(tagIdCounter++);

  const customHtmlTag = (
    id: string,
    name: string,
    html: string,
    triggerId: string,
    extraNotes?: string
  ): Record<string, unknown> => ({
    accountId: '0',
    containerId: '0',
    tagId: id,
    name,
    type: 'html',
    parameter: [
      { type: 'TEMPLATE', key: 'html', value: html },
      { type: 'BOOLEAN',  key: 'supportDocumentWrite', value: 'false' },
    ],
    fingerprint: fp,
    firingTriggerId: [triggerId],
    tagFiringOption: 'ONCE_PER_EVENT',
    monitoringMetadata: { type: 'MAP' },
    consentSettings: { consentStatus: 'NOT_SET' },
    ...(extraNotes ? { notes: extraNotes } : {}),
  });

  for (const pixelId of cleanPixels) {
    tags.push(
      customHtmlTag(
        nextTagId(),
        `Meta Pixel ${pixelId} — PageView`,
        PIXEL_TAG_HTML(pixelId, 'PageView'),
        ID.triggerPageView,
        `Generated by HitzLink for Pixel ${pixelId}. Edit eventID handling in HitzLink, then re-import.`
      )
    );
    tags.push(
      customHtmlTag(
        nextTagId(),
        `Meta Pixel ${pixelId} — CompleteRegistration`,
        PIXEL_TAG_HTML(pixelId, 'CompleteRegistration'),
        ID.triggerRegister,
      )
    );
    tags.push(
      customHtmlTag(
        nextTagId(),
        `Meta Pixel ${pixelId} — Purchase`,
        PIXEL_TAG_HTML(pixelId, 'Purchase', { withValue: true, withTransaction: true }),
        ID.triggerPurchase,
      )
    );
  }

  // ── GA4 (optional) ────────────────────────────────────────────────
  // We ship as Custom HTML rather than the GA4 native tag template
  // ("googtag") so the import works even on GTM containers that don't
  // have the GA4 template gallery enabled. Operators can swap to the
  // native template post-import for nicer config UI.
  if (ga4Id) {
    const ga4Loader = [
      '<script>',
      `(function(){if(window.__hzGa4Loaded)return;window.__hzGa4Loaded=true;`,
      `var s=document.createElement('script');s.async=true;`,
      `s.src='https://www.googletagmanager.com/gtag/js?id=${jsString(ga4Id)}';`,
      `document.head.appendChild(s);`,
      `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}`,
      `window.gtag=window.gtag||gtag;gtag('js',new Date());gtag('config','${jsString(ga4Id)}',{send_page_view:false});`,
      `})();`,
      `gtag('event','page_view');`,
      '</script>',
    ].join('\n');
    tags.push(
      customHtmlTag(nextTagId(), `GA4 ${ga4Id} — page_view`, ga4Loader, ID.triggerPageView)
    );

    const ga4SignUp = [
      '<script>',
      `if(window.gtag){gtag('event','sign_up',{method:'site_account'});}`,
      '</script>',
    ].join('\n');
    tags.push(
      customHtmlTag(nextTagId(), `GA4 ${ga4Id} — sign_up`, ga4SignUp, ID.triggerRegister)
    );

    const ga4Purchase = [
      '<script>',
      `if(window.gtag){gtag('event','purchase',{`,
      `  transaction_id:{{${GTM_DATALAYER_KEYS.transactionId}}}||'',`,
      `  value:Number({{${GTM_DATALAYER_KEYS.value}}})||0,`,
      `  currency:{{${GTM_DATALAYER_KEYS.currency}}}||'USD'`,
      `});}`,
      '</script>',
    ].join('\n');
    tags.push(
      customHtmlTag(nextTagId(), `GA4 ${ga4Id} — purchase`, ga4Purchase, ID.triggerPurchase)
    );
  }

  // ── Container Export envelope ─────────────────────────────────────
  // The shape mirrors what `GET /containers/{id}/versions/{ver}` returns
  // from GTM API v2 — and what GTM's "Export Container" produces. Only
  // the outer fields are required; everything else passes through.
  const containerVersion = {
    path: `accounts/0/containers/0/versions/0`,
    accountId: '0',
    containerId: '0',
    containerVersionId: '0',
    name: `HitzLink Managed — ${opts.pageTitle.slice(0, 80)}`,
    description:
      `Auto-generated by HitzLink for landing page ${opts.pageId}. ` +
      `Imports tags for ${cleanPixels.length} Meta Pixel(s)` +
      (ga4Id ? ` + GA4 ${ga4Id}` : '') +
      `. Reimport when the Pixel set or GA4 ID changes — the import` +
      ` tool will overwrite existing tags with the same IDs.`,
    container: {
      path: `accounts/0/containers/${safeGtm}`,
      accountId: '0',
      containerId: '0',
      name: 'HitzLink Container',
      publicId: safeGtm,
      usageContext: ['WEB'],
      fingerprint: fp,
      tagManagerUrl: '',
      features: {
        supportUserPermissions: true,
        supportEnvironments: true,
        supportWorkspaces: true,
        supportGtagConfigs: false,
        supportBuiltInVariables: true,
        supportClients: false,
        supportFolders: true,
        supportTags: true,
        supportTemplates: true,
        supportTriggers: true,
        supportVariables: true,
        supportVersions: true,
        supportZones: true,
        supportTransformations: false,
      },
    },
    tag: tags,
    trigger: triggers,
    variable: variables,
    builtInVariable: [
      { accountId: '0', containerId: '0', type: 'PAGE_URL',  name: 'Page URL'  },
      { accountId: '0', containerId: '0', type: 'PAGE_PATH', name: 'Page Path' },
      { accountId: '0', containerId: '0', type: 'EVENT',     name: 'Event'     },
    ],
    fingerprint: fp,
    tagManagerUrl: '',
  };

  const exportPayload = {
    exportFormatVersion: 2,
    exportTime: new Date().toISOString(),
    containerVersion,
  };

  return JSON.stringify(exportPayload, null, 2);
}

/**
 * Convenience helper: trigger a browser download of the template
 * JSON. Returns the filename used (so callers can show a confirm
 * toast). Caller must run inside a click handler to satisfy the
 * browser's download-gesture policy.
 */
export function downloadGtmContainerTemplate(opts: GtmTemplateOptions): string {
  const json = buildGtmContainerTemplate(opts);
  const safe = opts.pageTitle.replace(/[^a-z0-9]+/gi, '-').toLowerCase().slice(0, 60);
  const filename = `hitzlink-gtm-${safe || 'container'}.json`;
  const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return filename;
}
